import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Lock, MapPin, MessageSquare, Send, ShieldCheck } from "lucide-react";
import { useLang } from "../App";
import { membersI18n, propertiesI18n } from "../i18n";
import MemberShell from "../components/MemberShell";
import { commercialPricingMode, formatAvailability, formatCharges, formatPrice, formatPropertyPrice, isCommercialRental } from "./Properties";
import { getMemberToken, getMemberInfo, clearMemberSession } from "../memberAuth";
import { SoldRibbon } from "../components/SoldRibbon";
import { resolveImg } from "../images";

const API = process.env.REACT_APP_BACKEND_URL;

const darkInput =
  "w-full border-0 border-b border-ivory/25 bg-transparent px-0 py-2.5 text-sm text-ivory outline-none transition-colors duration-300 placeholder:text-ivory/30 focus:border-champagne";

function Watermark({ text }) {
  return (
    <div
      className="pointer-events-none absolute inset-0 z-10 flex flex-wrap content-center justify-center gap-x-20 gap-y-16 overflow-hidden"
      aria-hidden="true"
    >
      {Array.from({ length: 12 }).map((_, i) => (
        <span
          key={i}
          className="-rotate-12 select-none whitespace-nowrap text-[10px] font-light uppercase tracking-[0.5em] text-ivory/[0.06]"
        >
          {text}
        </span>
      ))}
    </div>
  );
}

export default function MemberDetail() {
  const { lang, t } = useLang();
  const m = membersI18n[lang];
  const p = propertiesI18n[lang];
  const c = t.contact;
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(undefined);
  const [imgIdx, setImgIdx] = useState(0);
  const [hidden, setHidden] = useState(false);
  const [convo, setConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const [chatBusy, setChatBusy] = useState(false);
  const pinged = useRef(false);
  const info = getMemberInfo();

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!getMemberToken()) {
      navigate("/espace");
      return;
    }
    fetch(`${API}/api/member/properties`, { headers: { Authorization: `Bearer ${getMemberToken()}` } })
      .then((r) => {
        if (!r.ok) throw new Error("auth");
        return r.json();
      })
      .then((d) => setProperty(d.properties.find((x) => x.id === id) || null))
      .catch(() => {
        clearMemberSession();
        navigate("/espace");
      });
  }, [id, navigate]);

  useEffect(() => {
    if (!property || pinged.current) return;
    pinged.current = true;
    fetch(`${API}/api/member/properties/${property.id}/view`, {
      method: "POST",
      headers: { Authorization: `Bearer ${getMemberToken()}` },
    }).catch(() => {});
  }, [property]);

  useEffect(() => {
    const onVis = () => setHidden(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const location = useLocation();

  const openChat = async () => {
    if (!property) return;
    setChatOpen(true);
    if (convo) return;
    setChatBusy(true);
    try {
      const res = await fetch(`${API}/api/member/conversations`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getMemberToken()}` },
        body: JSON.stringify({ property_id: property.id }),
      });
      if (!res.ok) throw new Error("failed");
      const data = await res.json();
      setConvo(data.conversation);
      const msgs = await fetch(`${API}/api/member/conversations/${data.conversation.id}`, {
        headers: { Authorization: `Bearer ${getMemberToken()}` },
      });
      if (msgs.ok) setMessages((await msgs.json()).messages);
    } catch {
      /* silent */
    } finally {
      setChatBusy(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!draft.trim() || !convo) return;
    setChatBusy(true);
    try {
      const res = await fetch(`${API}/api/member/conversations/${convo.id}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getMemberToken()}` },
        body: JSON.stringify({ body: draft }),
      });
      if (!res.ok) throw new Error("failed");
      const data = await res.json();
      setMessages((ms) => [...ms, data.message]);
      setDraft("");
    } catch {
      /* silent */
    } finally {
      setChatBusy(false);
    }
  };

  useEffect(() => {
    if (property && location.state?.openChat) {
      openChat().finally(() => {
        setTimeout(() => {
          document.querySelector('[data-testid="member-chat"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 150);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property]);

  if (property === undefined)
    return (
      <MemberShell>
        <p className="px-6 py-24 text-sm text-ivory/50 md:px-12 lg:px-20">…</p>
      </MemberShell>
    );

  if (!property)
    return (
      <MemberShell>
        <div data-testid="member-property-not-found" className="px-6 py-24 md:px-12 lg:px-20">
          <p className="font-serif text-2xl italic text-ivory/60">{p.notFound}</p>
          <Link to="/espace/objets" className="group mt-8 inline-flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.25em] text-ivory/50 hover:text-champagne">
            <ArrowLeft size={14} className="transition-transform duration-500 group-hover:-translate-x-1" /> {m.backToArea}
          </Link>
        </div>
      </MemberShell>
    );

  const isRental = property.listing_type === "rental";
  const isRented = isRental && property.availability_status === "rented";
  const commercialRental = isCommercialRental(property);
  const commercialRateRental = commercialRental && commercialPricingMode(property) === "per_sqm_year";
  const desc = lang !== "fr" && property[`description_${lang}`] ? property[`description_${lang}`] : property.description;
  const feats = lang !== "fr" && (property[`features_${lang}`] || []).length > 0 ? property[`features_${lang}`] : property.features;
  const title = lang !== "fr" && property[`title_${lang}`] ? property[`title_${lang}`] : property.title;
  const extras = lang !== "fr" && (property[`extra_facts_${lang}`] || []).length > 0 ? property[`extra_facts_${lang}`] : property.extra_facts;

  const facts = [
    [p.categoryLabel, p.categories[property.category]],
    property.floor ? [p.floorLabel, property.floor] : null,
    property.rooms ? [p.roomsLabel, `${property.rooms} ${p.roomsUnit}`] : null,
    property.usable_area ? [p.usableAreaLabel, `${property.usable_area} ${p.areaUnit}`] : null,
    property.land_area ? [p.landAreaLabel, `${property.land_area} ${p.areaUnit}`] : null,
    property.area && !commercialRental ? [p.livingAreaLabel, `${property.area} ${p.areaUnit}`] : null,
    property.year_built ? [p.yearBuiltLabel, property.year_built] : null,
    property.renovation_year ? [p.renovationLabel, property.renovation_year] : null,
    [p.locationFactLabel, `${property.zip_code} ${property.city}`],
    ...(extras || [])
      .map((x) => {
        const idx = x.includes(":") ? x.indexOf(":") : x.indexOf("=");
        if (idx < 1) return null;
        return [x.slice(0, idx).trim(), x.slice(idx + 1).trim()];
      })
      .filter(Boolean),
  ].filter(Boolean);

  const watermarkText = info ? `${info.email} · FLEN` : "FLEN";

  return (
    <MemberShell>
      <section
        data-testid="member-property-detail"
        onContextMenu={(e) => e.preventDefault()}
        className={`relative px-6 pb-28 pt-12 transition-[filter] duration-500 md:px-12 lg:px-20 ${hidden ? "blur-lg" : ""}`}
      >
        <Link to="/espace/objets" data-testid="member-property-back"
          className="group relative z-30 inline-flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.25em] text-ivory/50 transition-colors duration-300 hover:text-champagne">
          <ArrowLeft size={14} className="transition-transform duration-500 group-hover:-translate-x-1" /> {m.backToArea}
        </Link>

        <div className="relative z-30 mt-10">
          <p className="flex items-center gap-3 text-[11px] uppercase tracking-[0.25em] text-champagne">
            <Lock size={12} /> Off-market · {isRental ? p.rental : p.sale} · {p.categories[property.category]}
          </p>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl font-medium leading-tight text-ivory sm:text-5xl">{title}</h1>
          <p className="mt-4 flex items-center gap-2 text-sm font-light text-ivory/55">
            <MapPin size={14} /> {property.zip_code} {property.city}
          </p>
          {property.ref && (
            <p className="mt-2 text-[11px] uppercase tracking-[0.25em] text-ivory/35" data-testid="member-property-ref">
              {property.ref}
            </p>
          )}
          <p className="mt-5 inline-flex items-center gap-2 border border-champagne/40 px-4 py-2 text-[10px] uppercase tracking-[0.18em] text-champagne">
            <ShieldCheck size={13} /> {m.protectedNote}
          </p>
        </div>

        {property.images && property.images.length > 0 && (
          <div className="relative z-30 mt-12 select-none">
            <div className="relative overflow-hidden bg-ivory/5">
              {property.sold ? (
                <SoldRibbon label={p.sold} testid="member-detail-sold" />
              ) : isRented ? (
                <SoldRibbon label={p.rented} testid="member-detail-rented" />
              ) : null}
              <img src={resolveImg(property.images[imgIdx])} alt={property.title} draggable={false} data-testid="member-property-image"
                className="h-auto max-h-[55vh] w-full select-none object-contain md:max-h-[68vh]" />
              <div className="absolute inset-0" />
              <Watermark text={watermarkText} />
            </div>
            <p className="mt-3 text-right text-[10px] font-light uppercase tracking-[0.25em] text-ivory/35" data-testid="member-watermark-caption">
              {m.watermarkCaption.replace("{email}", info?.email || "")}
            </p>
            {property.images.length > 1 && (
              <div className="mt-4 flex gap-3 overflow-x-auto pb-2">
                {property.images.map((src, i) => (
                  <button key={src} data-testid={`member-thumb-${i}`} onClick={() => setImgIdx(i)}
                    className={`h-16 w-24 shrink-0 overflow-hidden border transition-all duration-300 ${
                      i === imgIdx ? "border-champagne" : "border-transparent opacity-60 hover:opacity-100"
                    }`}>
                    <img src={resolveImg(src)} alt="" draggable={false} className="h-full w-full select-none object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="relative z-30 mt-16 grid gap-16 lg:grid-cols-[1fr_360px]">
          <div>
            <h2 className="text-[11px] font-medium uppercase tracking-[0.3em] text-champagne">{p.factsTitle}</h2>
            <dl className="mt-6 divide-y divide-ivory/10 border-y border-ivory/10" data-testid="member-facts">
              {facts.map(([k, v]) => (
                <div key={k} className="flex justify-between gap-6 py-4 text-sm">
                  <dt className="font-light text-ivory/50">{k}</dt>
                  <dd className="text-right font-medium text-ivory">{v}</dd>
                </div>
              ))}
            </dl>
            {desc && (
              <div className="mt-16">
                <h2 className="text-[11px] font-medium uppercase tracking-[0.3em] text-champagne">{p.descriptionTitle}</h2>
                <p className="mt-6 max-w-2xl whitespace-pre-line text-base font-light leading-relaxed text-ivory/70">
                  {desc}
                </p>
              </div>
            )}
            {feats && feats.length > 0 && (
              <div className="mt-16">
                <h2 className="text-[11px] font-medium uppercase tracking-[0.3em] text-champagne">{p.featuresTitle}</h2>
                <ul className="mt-6 grid gap-x-10 sm:grid-cols-2">
                  {feats.map((f, i) => (
                    <li key={i} className="flex items-center gap-3 border-b border-ivory/10 py-3.5 text-sm font-light text-ivory/70">
                      <span className="h-1 w-1 rotate-45 bg-champagne" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <aside className="lg:sticky lg:top-8 lg:self-start">
            <div className="border border-ivory/15 p-8" data-testid="member-price-box">
              <p className="text-[10px] uppercase tracking-[0.25em] text-ivory/45">{isRental ? (commercialRateRental ? p.annualRentLabel : p.grossRentLabel) : p.priceLabel}</p>
              <p className="mt-2 font-serif text-3xl font-medium text-ivory" data-testid="member-property-price">{formatPropertyPrice(property, p)}</p>
              {isRental && (
                <dl className="mt-6 space-y-3 border-t border-ivory/10 pt-5 text-sm" data-testid="member-rental-breakdown">
                  {commercialRateRental && Number(property.rental_price_per_sqm_year) > 0 && (
                    <div className="flex justify-between gap-4">
                      <dt className="font-light text-ivory/50">{p.rentalRateLabel}</dt>
                      <dd className="font-medium text-ivory" data-testid="member-rental-rate">{formatPrice(property.rental_price_per_sqm_year)} {p.perSqmYear}</dd>
                    </div>
                  )}
                  <div className="flex justify-between gap-4">
                    <dt className="font-light text-ivory/50">{p.netRentLabel}</dt>
                    <dd className="font-medium text-ivory" data-testid="member-rental-net">{formatPrice(property.monthly_net_rent || 0)} {p.perMonth}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="font-light text-ivory/50">{p.chargesLabel}</dt>
                    <dd className="font-medium text-ivory" data-testid="member-rental-charges">{formatCharges(property, p)}</dd>
                  </div>
                  {commercialRateRental && (
                    <div className="flex justify-between gap-4">
                      <dt className="font-light text-ivory/50">{p.grossRentLabel}</dt>
                      <dd className="font-medium text-ivory" data-testid="member-rental-gross">{formatPrice(property.monthly_gross_rent || 0)} {p.perMonth}</dd>
                    </div>
                  )}
                  {property.rental_deposit > 0 && (
                    <div className="flex justify-between gap-4 border-t border-ivory/10 pt-3">
                      <dt className="font-light text-ivory/50">{p.depositLabel}</dt>
                      <dd className="font-medium text-ivory" data-testid="member-rental-deposit">{formatPrice(property.rental_deposit)}</dd>
                    </div>
                  )}
                </dl>
              )}
              {(isRental || property.availability) && (
                <div className="mt-6 border-t border-ivory/10 pt-4">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-ivory/45">{p.availabilityLabel}</p>
                  <p className="mt-1 text-sm font-light text-ivory/80" data-testid="member-property-availability">
                    {isRental ? formatAvailability(property, p, lang) : property.availability}
                  </p>
                </div>
              )}
            </div>

            <div className="mt-6 border border-champagne/40 p-8" data-testid="member-chat">
              {!chatOpen ? (
                <div>
                  <h3 className="font-serif text-2xl font-medium leading-snug text-ivory">{p.inquiryTitle}</h3>
                  <p className="mt-2 text-xs font-light leading-relaxed text-ivory/55">{p.inquirySub}</p>
                  <button
                    data-testid="member-interested-button"
                    onClick={openChat}
                    className="group mt-8 flex w-full items-center justify-center gap-3 bg-champagne px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-ink transition-colors duration-500 hover:bg-ivory"
                  >
                    {m.interested}
                    <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
                  </button>
                  <p data-testid="member-chat-reply-time" className="mt-5 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.18em] text-ivory/35">
                    <Clock size={11} /> {m.replyTime}
                  </p>
                </div>
              ) : (
                <div data-testid="member-chat-thread">
                  <div className="flex items-center justify-between border-b border-ivory/10 pb-4">
                    <div>
                      <h3 className="font-serif text-xl font-medium text-ivory">{m.messages}</h3>
                      <p data-testid="member-chat-reply-time-open" className="mt-1.5 flex items-center gap-1.5 text-[9px] uppercase tracking-[0.1em] text-ivory/35">
                        <Clock size={10} /> {m.replyTime}
                      </p>
                    </div>
                    <MessageSquare size={15} className="text-champagne" />
                  </div>
                  <div className="mt-5 max-h-72 space-y-4 overflow-y-auto pr-1" data-testid="member-chat-messages">
                    {messages.length === 0 && (
                      <p className="text-xs font-light italic text-ivory/45">{m.chatEmpty}</p>
                    )}
                    {messages.map((msg) => (
                      <div key={msg.id} className={`flex ${msg.sender === "member" ? "justify-end" : "justify-start"}`}>
                        <div
                          className={`max-w-[85%] px-4 py-3 text-sm font-light leading-relaxed ${
                            msg.sender === "member" ? "bg-champagne/15 text-ivory" : "border border-ivory/15 text-ivory/80"
                          }`}
                        >
                          <p className="mb-1 text-[9px] uppercase tracking-[0.2em] text-ivory/40">
                            {msg.sender === "member" ? m.you : "FLEN"}
                          </p>
                          {msg.body}
                        </div>
                      </div>
                    ))}
                  </div>
                  <form onSubmit={sendMessage} className="mt-5 flex items-end gap-3 border-t border-ivory/10 pt-5">
                    <textarea
                      rows={2}
                      data-testid="member-chat-input"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      placeholder={m.typeMessage}
                      className={`${darkInput} flex-1 resize-none`}
                    />
                    <button
                      type="submit"
                      data-testid="member-chat-send"
                      disabled={chatBusy || !draft.trim()}
                      className="flex items-center gap-2 bg-champagne px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-ink transition-colors duration-500 hover:bg-ivory disabled:opacity-50"
                    >
                      <Send size={13} /> {m.send}
                    </button>
                  </form>
                </div>
              )}
            </div>
          </aside>
        </div>
      </section>
    </MemberShell>
  );
}
